import React, { useEffect, useRef, useState } from "react";
// @ts-ignore
import QRImage from "../../assets/QR.png";
import { FaWhatsapp } from "react-icons/fa";

/* ─────────────────────────────────────────────
   HOOKS
───────────────────────────────────────────── */
function useWindowWidth() {
  const [w, setW] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setTimeout(() => setVis(true), delay);
          obs.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return { ref, vis };
}

/* ─────────────────────────────────────────────
   TOKENS
───────────────────────────────────────────── */
const G = "#6cbb23";
const T = {
  zh: { fontFamily: "'Fraunces',Georgia,serif" },
  zb: { fontFamily: "'Plus Jakarta Sans',sans-serif" },
};

const revealStyle = (vis: boolean, delay = 0): React.CSSProperties => ({
  opacity: vis ? 1 : 0,
  transform: vis ? "translateY(0)" : "translateY(20px)",
  transition: `opacity .6s ${delay}ms cubic-bezier(.22,.68,0,1.2), transform .6s ${delay}ms cubic-bezier(.22,.68,0,1.2)`,
});

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const FontLink = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,800;0,9..144,900;1,9..144,800;1,9..144,900&family=Plus+Jakarta+Sans:wght@300;400;500;600&display=swap');

    @keyframes cta-blink  { 0%,100%{opacity:1} 50%{opacity:.35} }
    @keyframes cta-pulse  { 0%,100%{transform:scale(1);opacity:.6} 50%{transform:scale(1.9);opacity:0} }
    @keyframes cta-float  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
    @keyframes cta-fadein { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
    @keyframes cta-shimmer{
      0%  { background-position:-200% center; }
      100%{ background-position: 200% center; }
    }
    @keyframes cta-scanline {
      0%   { transform: translateY(-100%); }
      100% { transform: translateY(500%);  }
    }

    .cta-wa-btn {
      background: ${G};
      color: #fff; border: none; cursor: pointer;
      transition: transform .2s, box-shadow .2s;
      position: relative; overflow: hidden;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 700;
    }
    .cta-wa-btn::before {
      content: '';
      position: absolute; inset: 0;
      background: linear-gradient(135deg, rgba(255,255,255,.18) 0%, transparent 60%);
      opacity: 0; transition: opacity .2s;
    }
    .cta-wa-btn:hover { transform: translateY(-3px); box-shadow: 0 16px 48px rgba(108,187,35,.5); }
    .cta-wa-btn:hover::before { opacity: 1; }
    .cta-wa-btn:active { transform: scale(.97); }

    .cta-qr-card {
      transition: transform .22s, box-shadow .22s;
    }
    .cta-qr-card:hover { transform: translateY(-3px) scale(1.01); box-shadow: 0 20px 60px rgba(0,0,0,.5) !important; }

    .cta-trust-item {
      transition: border-color .18s, background .18s;
    }
    .cta-trust-item:hover {
      border-color: rgba(108,187,35,.4) !important;
      background: rgba(108,187,35,.05) !important;
    }

    .cta-shimmer-text {
      background: linear-gradient(90deg, #6cbb23 0%, #a8e063 35%, #6cbb23 55%, #4d8f10 100%);
      background-size: 200% auto;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: cta-shimmer 3.5s linear infinite;
    }

    .cta-float { animation: cta-float 4s ease-in-out infinite; }

    .cta-stat-enter { animation: cta-fadein .45s cubic-bezier(.22,.68,0,1.2) both; }
  `}</style>
);

/* ─────────────────────────────────────────────
   LIVE INDICATOR
───────────────────────────────────────────── */
const LiveDot = () => (
  <div style={{ position: "relative", width: 10, height: 10, flexShrink: 0 }}>
    <div style={{
      position: "absolute", inset: 0, borderRadius: "50%", background: G,
      animation: "cta-pulse 2s ease-out infinite",
    }}/>
    <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: G }}/>
  </div>
);

/* ─────────────────────────────────────────────
   QR CARD COMPONENT
───────────────────────────────────────────── */
const QRCard = ({ vis }: { vis: boolean }) => (
  <div style={{ ...revealStyle(vis, 180), display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>

    {/* Floating phone frame */}
    <div className="cta-qr-card cta-float" style={{
      background: "linear-gradient(160deg,#1a2a1a,#111e11)",
      border: "1.5px solid rgba(108,187,35,.2)",
      borderRadius: 24,
      padding: "18px 18px 14px",
      boxShadow: "0 24px 64px rgba(0,0,0,.5), 0 4px 12px rgba(0,0,0,.3)",
      position: "relative", overflow: "hidden",
    }}>
      {/* Scan line */}
      <div style={{
        position: "absolute", left: 0, right: 0, height: 40, zIndex: 3,
        background: "linear-gradient(180deg,transparent,rgba(108,187,35,.06),transparent)",
        animation: "cta-scanline 3.5s ease-in-out infinite",
        pointerEvents: "none",
      }}/>

      {/* Phone notch */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
        <div style={{ width: 60, height: 5, borderRadius: 3, background: "rgba(255,255,255,.12)" }}/>
      </div>

      {/* WhatsApp header */}
      <div style={{
        background: "#0d1f0d",
        borderRadius: "12px 12px 0 0",
        padding: "10px 12px",
        display: "flex", alignItems: "center", gap: 8,
        borderBottom: "1px solid rgba(255,255,255,.06)",
        marginBottom: 0,
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: "50%",
          background: G, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <FaWhatsapp style={{ color: "#fff", fontSize: 13 }} />
        </div>
        <div>
          <div style={{ ...T.zb, fontSize: 11, fontWeight: 600, color: "#fff" }}>Zafflo Team</div>
          <div style={{ ...T.zb, fontSize: 9, color: G, display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ animation: "cta-blink 1.6s ease-in-out infinite", display: "inline-block", width: 5, height: 5, borderRadius: "50%", background: G }}/>
            online now
          </div>
        </div>
      </div>

      {/* QR code */}
      <div style={{
        background: "#fff",
        borderRadius: "0 0 12px 12px",
        padding: 12,
        position: "relative", zIndex: 2,
      }}>
        <img
          src={QRImage}
          alt="Scan to book demo on WhatsApp"
          style={{ width: 160, height: 160, display: "block", borderRadius: 8 }}
        />
      </div>

      {/* Scan prompt */}
      <div style={{
        marginTop: 12, textAlign: "center",
        ...T.zb, fontSize: 10, color: "rgba(255,255,255,.35)",
      }}>
        Point your camera at the QR
      </div>
    </div>

    {/* Badge below QR */}
    <div style={{
      marginTop: 18,
      display: "inline-flex", alignItems: "center", gap: 7,
      background: "rgba(108,187,35,.1)",
      border: "1px solid rgba(108,187,35,.25)",
      borderRadius: 999, padding: "6px 14px",
      ...T.zb, fontSize: 11, fontWeight: 600, color: G,
    }}>
      <LiveDot />
      Team responds within minutes
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const CTA = () => {
  const width    = useWindowWidth();
  const isMobile = width < 768;

  const left  = useReveal(0);
  const right = useReveal(120);

  const trustItems = [
    { emoji: "🚫", text: "No spam ever"         },
    { emoji: "💬", text: "No sales pressure"    },
    { emoji: "🆓", text: "Completely free demo" },
    { emoji: "⚡", text: "< 30 sec to connect"  },
  ];

  const demoHighlights = [
    "Live walkthrough of your digital menu",
    "See WhatsApp orders in real time",
    "Analytics, GST & appointment demo",
    "Pricing & onboarding Q&A",
  ];

  return (
    <section
      id="BookDemo"
      style={{
        background: "linear-gradient(180deg, #0c1a0c 0%, #111e11 100%)",
        position: "relative", overflow: "hidden",
        padding: isMobile ? "72px 0 80px" : "88px 0 96px",
        fontFamily: "'Plus Jakarta Sans',sans-serif",
      }}
    >
      <FontLink />

      {/* Grid texture */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(rgba(108,187,35,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(108,187,35,.04) 1px,transparent 1px)",
        backgroundSize: "48px 48px",
      }}/>

      {/* Corner cross accents */}
      {[[60,60],[60,-1],[-1,60],[-1,-1]].map(([r,b],i) => (
        <div key={i} style={{
          position: "absolute",
          ...(b === -1 ? { bottom: 48 } : { top: 48 }),
          ...(r === -1 ? { right: 48 } : { left: 48 }),
          width: 20, height: 20, pointerEvents: "none",
        }}>
          <div style={{ position:"absolute",left:"50%",top:0,bottom:0,width:"1.5px",background:"rgba(255,255,255,.08)",transform:"translateX(-50%)" }}/>
          <div style={{ position:"absolute",top:"50%",left:0,right:0,height:"1.5px",background:"rgba(255,255,255,.08)",transform:"translateY(-50%)" }}/>
        </div>
      ))}

      {/* Large glow centre-top */}
      <div style={{
        position: "absolute", top: -120, left: "50%", transform: "translateX(-50%)",
        width: 700, height: 500, borderRadius: "50%",
        background: "radial-gradient(ellipse,rgba(108,187,35,.12) 0%,transparent 70%)",
        pointerEvents: "none",
      }}/>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px", position: "relative", zIndex: 1 }}>

        <div style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 52 : 72,
          alignItems: isMobile ? "stretch" : "center",
        }}>

          {/* ── LEFT — Editorial copy ── */}
          <div ref={left.ref} style={{ ...revealStyle(left.vis), flex: 1, minWidth: 0 }}>

            {/* Eyebrow */}
            <div style={{ marginBottom: 24 }}>
              <span style={{
                ...T.zb,
                display: "inline-flex", alignItems: "center", gap: 8,
                background: "rgba(108,187,35,.12)", border: "1px solid rgba(108,187,35,.28)",
                borderRadius: 999, padding: "5px 16px",
                fontSize: 11, fontWeight: 600, color: G,
                letterSpacing: ".08em", textTransform: "uppercase" as const,
              }}>
                <LiveDot />
                Book a Free Demo
              </span>
            </div>

            {/* Headline */}
            <h2 style={{
              ...T.zh,
              fontSize: "clamp(32px,4vw,52px)",
              fontWeight: 900, fontStyle: "italic",
              lineHeight: 1.07, letterSpacing: "-0.03em",
              color: "#fff", marginBottom: 20,
            }}>
              See Zafflo in action.
              <br/>
              <span className="cta-shimmer-text">Live, for free,</span>
              <br/>
              <span style={{ color: "rgba(255,255,255,.55)" }}>in under 15 minutes.</span>
            </h2>

            {/* Body */}
            <p style={{
              ...T.zb, fontSize: 15, color: "rgba(255,255,255,.45)",
              fontWeight: 300, lineHeight: 1.8, maxWidth: 420, marginBottom: 32,
            }}>
              Our team will personally walk you through the full platform — orders,
              analytics, GST billing, and appointments — tailored to your business type.
            </p>

            {/* Demo highlights */}
            <div style={{ marginBottom: 36, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ ...T.zb, fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,.25)", letterSpacing: ".1em", textTransform: "uppercase" as const, marginBottom: 4 }}>
                What you'll see in the demo
              </div>
              {demoHighlights.map((item, i) => (
                <div key={item} className="cta-stat-enter" style={{ animationDelay: `${i * 80 + 200}ms`, display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: "50%",
                    background: "rgba(108,187,35,.15)", border: "1px solid rgba(108,187,35,.25)",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                      <path d="M1.5 4.5l2 2L7.5 2" stroke={G} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span style={{ ...T.zb, fontSize: 13.5, color: "rgba(255,255,255,.6)", fontWeight: 300 }}>{item}</span>
                </div>
              ))}
            </div>

            {/* Trust grid */}
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(2,1fr)",
              gap: 8,
            }}>
              {trustItems.map(({ emoji, text }) => (
                <div key={text} className="cta-trust-item" style={{
                  background: "rgba(255,255,255,.04)",
                  border: "1px solid rgba(255,255,255,.08)",
                  borderRadius: 12, padding: "10px 14px",
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  <span style={{ fontSize: 14 }}>{emoji}</span>
                  <span style={{ ...T.zb, fontSize: 12, color: "rgba(255,255,255,.5)", fontWeight: 400 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── RIGHT — Action panel ── */}
          <div ref={right.ref} style={{ ...revealStyle(right.vis, 120), flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>

            {/* Primary WhatsApp CTA */}
            <div style={{
              background: "rgba(255,255,255,.04)",
              border: "1.5px solid rgba(108,187,35,.2)",
              borderRadius: 24, padding: "36px 32px",
              display: "flex", flexDirection: "column", alignItems: "center",
              gap: 0, width: isMobile ? "100%" : 340,
              boxShadow: "0 16px 56px rgba(0,0,0,.4)",
            }}>

              {/* Icon */}
              <div style={{
                width: 64, height: 64, borderRadius: 18,
                background: "rgba(108,187,35,.15)",
                border: "1.5px solid rgba(108,187,35,.3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: 20,
              }}>
                <FaWhatsapp style={{ color: G, fontSize: 28 }} />
              </div>

              <div style={{ ...T.zh, fontSize: 22, fontWeight: 900, fontStyle: "italic", color: "#fff", textAlign: "center", lineHeight: 1.15, marginBottom: 10 }}>
                Chat with us now
              </div>
              <p style={{ ...T.zb, fontSize: 13, color: "rgba(255,255,255,.4)", textAlign: "center", lineHeight: 1.65, fontWeight: 300, marginBottom: 28, maxWidth: 240 }}>
                Message us on WhatsApp and our team will personally walk you through everything.
              </p>

              {/* WhatsApp button */}
              <a
                href="https://wa.me/918097588099?text=Hi%20I%20am%20interested%20to%20know%20more%20about%20Zafflo."
                target="_blank"
                rel="noopener noreferrer"
                style={{ width: "100%", textDecoration: "none" }}
              >
                <button className="cta-wa-btn" style={{
                  width: "100%", padding: "16px 28px",
                  borderRadius: 12, fontSize: 15,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 9,
                }}>
                  <FaWhatsapp style={{ fontSize: 18 }} />
                  Book Free Demo on WhatsApp
                </button>
              </a>

              {/* Divider */}
              <div style={{
                width: "100%", display: "flex", alignItems: "center", gap: 12,
                margin: "24px 0",
              }}>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,.08)" }}/>
                <span style={{ ...T.zb, fontSize: 11, color: "rgba(255,255,255,.2)" }}>or scan QR on mobile</span>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,.08)" }}/>
              </div>

              {/* QR code — compact */}
              <div style={{
                background: "#fff", borderRadius: 14, padding: 10,
                boxShadow: "0 4px 20px rgba(0,0,0,.3)",
              }}>
                <img
                  src={QRImage}
                  alt="Scan QR to book free demo"
                  style={{ width: 120, height: 120, display: "block", borderRadius: 6 }}
                />
              </div>
              <div style={{ ...T.zb, fontSize: 10, color: "rgba(255,255,255,.22)", marginTop: 10, textAlign: "center" }}>
                Opens WhatsApp · Takes &lt; 30 seconds
              </div>
            </div>

            {/* Live status below card */}
            <div style={{
              marginTop: 16, display: "flex", alignItems: "center", gap: 8,
              ...T.zb, fontSize: 12, color: "rgba(255,255,255,.3)",
            }}>
              <LiveDot />
              <span>Typically replies in under 5 minutes</span>
            </div>

          </div>
        </div>

      

      </div>
    </section>
  );
};

export default CTA;